import { useEffect, useMemo, useState } from 'react';
import { adminApi } from '@/lib/api';
import { Calendar, QrCode, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

export default function ReportsQrCodesPage() {
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);

  useEffect(() => {
    fetchReport();
  }, [period]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getQrCodeReport(period);
      setReportData(response.data);
    } catch (error) {
      toast.error('Failed to fetch QR report');
    } finally {
      setLoading(false);
    }
  };

  const dailyScans = useMemo(
    () => (reportData?.daily_scans || []).map((item) => ({
      ...item,
      scans: Number(item.scans || 0),
    })),
    [reportData]
  );

  const topQr = useMemo(
    () => (reportData?.top_qr || []).map((item) => ({
      ...item,
      scans: Number(item.scans || 0),
      label: item.name || item.code || '-'
    })),
    [reportData]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#C97A5E]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-qr-codes-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#2D2420]">Reports · QR Codes</h1>
          <p className="text-[#A8A8A8] mt-1">Scans and engagement from QR codes</p>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-[#A8A8A8]" />
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-40 border-[#E5E5E5]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">Last 7 Days</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-white border-[#E5E5E5]">
          <CardContent className="p-4">
            <p className="text-xs text-[#A8A8A8]">Total Scans</p>
            <p className="text-2xl font-bold text-[#2D2420]">{reportData?.total_scans || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#E5E5E5]">
          <CardContent className="p-4">
            <p className="text-xs text-[#A8A8A8]">Unique QR Codes</p>
            <p className="text-2xl font-bold text-[#2D2420]">{reportData?.unique_qr || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#E5E5E5]">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#C97A5E]/10 flex items-center justify-center">
              <QrCode className="w-5 h-5 text-[#C97A5E]" />
            </div>
            <div>
              <p className="text-xs text-[#A8A8A8]">Top QR</p>
              <p className="text-sm font-semibold text-[#2D2420]">
                {topQr[0]?.label || '-'}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-white border-[#E5E5E5]">
          <CardHeader>
            <CardTitle className="text-lg text-[#2D2420]">Daily Scans</CardTitle>
          </CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={dailyScans}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                <Tooltip />
                <Line type="monotone" dataKey="scans" stroke="#C97A5E" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="bg-white border-[#E5E5E5]">
          <CardHeader>
            <CardTitle className="text-lg text-[#2D2420]">Top QR Codes</CardTitle>
          </CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={topQr} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                <XAxis type="number" allowDecimals={false} />
                <YAxis type="category" dataKey="label" width={140} tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="scans" fill="#C97A5E" radius={[6, 6, 6, 6]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
